import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import {
  useApi,
  type CleaningTeamMemberRequest,
  type CleaningTeamResponse,
  type CreateCleaningTeamRequest,
} from '@/shared/api';
import { companyKeys } from '@/features/team/queries';
import { CleaningTeamFormFields } from './CleaningTeamFormFields';

export type CleaningTeamFormValues = CreateCleaningTeamRequest & { isActive: boolean };

const empty: CleaningTeamFormValues = {
  name: '',
  companyId: null,
  members: [],
  propertyGroupIds: [],
  isActive: true,
};

function initial(team: CleaningTeamResponse | null): CleaningTeamFormValues {
  if (!team) return empty;
  return {
    name: team.name,
    companyId: team.companyId,
    members: team.members
      .filter((member) => member.status === 'Active')
      .map(({ userId, role }) => ({ userId, role })),
    propertyGroupIds: team.propertyGroupIds,
    isActive: team.status === 'Active',
  };
}

export function CleaningTeamEditorDialog({
  team,
  open,
  loading,
  onOpenChange,
  onSubmit,
}: {
  team: CleaningTeamResponse | null;
  open: boolean;
  loading: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: CleaningTeamFormValues) => void;
}) {
  const api = useApi();
  const [values, setValues] = useState<CleaningTeamFormValues>(() => initial(team));
  const companies = useQuery({
    queryKey: companyKeys.list({ page: 1, pageSize: 200 }),
    queryFn: () => api.companies.list({ page: 1, pageSize: 200 }),
    enabled: open,
  });
  const users = useQuery({
    queryKey: ['users', 'cleaning-team-candidates'],
    queryFn: () => api.users.list({ page: 1, pageSize: 200 }),
    enabled: open,
  });
  const objects = useQuery({
    queryKey: ['property-groups'],
    queryFn: () => api.propertyGroups.list(),
    enabled: open,
  });
  useEffect(() => {
    if (open) setValues(initial(team));
  }, [open, team]);

  const toggleMember = (userId: string) =>
    setValues((current) => ({
      ...current,
      members: current.members.some((member) => member.userId === userId)
        ? current.members.filter((member) => member.userId !== userId)
        : [...current.members, { userId, role: 'Member' }],
    }));
  const setMemberRole = (userId: string, role: CleaningTeamMemberRequest['role']) =>
    setValues((current) => ({
      ...current,
      members: current.members.map((member) =>
        member.userId === userId ? { ...member, role } : member,
      ),
    }));
  const toggleObject = (id: string) =>
    setValues((current) => ({
      ...current,
      propertyGroupIds: current.propertyGroupIds.includes(id)
        ? current.propertyGroupIds.filter((item) => item !== id)
        : [...current.propertyGroupIds, id],
    }));

  const busy = companies.isLoading || users.isLoading || objects.isLoading;
  const sourcesFailed = companies.isError || users.isError || objects.isError;
  const cleaningCompanies = (companies.data?.items ?? []).filter(
    (company) => company.type === 'CleaningCompany',
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>{team ? 'Reinigungsteam bearbeiten' : 'Neues Reinigungsteam'}</DialogTitle>
          <DialogDescription>
            Änderungen werden atomar gespeichert und machen betroffene Sitzungen sofort ungültig.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (values.name.trim() && !busy && !sourcesFailed) onSubmit(values);
          }}
        >
          {sourcesFailed && (
            <p role="alert" className="text-sealwax px-6 pt-4 text-sm">
              Teamquellen konnten nicht vollständig geladen werden. Bitte schließen Sie den Dialog
              und versuchen Sie es erneut.
            </p>
          )}
          <CleaningTeamFormFields
            values={values}
            busy={busy}
            companies={cleaningCompanies}
            users={users.data?.items ?? []}
            propertyGroups={objects.data ?? []}
            onNameChange={(name) => setValues({ ...values, name })}
            onCompanyChange={(companyId) => setValues({ ...values, companyId })}
            onActiveChange={(isActive) => setValues({ ...values, isActive })}
            onToggleMember={toggleMember}
            onMemberRoleChange={setMemberRole}
            onTogglePropertyGroup={toggleObject}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="brand"
              loading={loading}
              disabled={busy || sourcesFailed || !values.name.trim()}
            >
              Team speichern
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
